import { useState } from "react";
import { ReasonCancelled, isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3 } from "lucide-react";
import { pricingApi, CHANNEL_LABEL, type Channel } from "@/services/pricing";
import { Can } from "@/auth/permissions";
import { PageShell, Pill, LoadingState } from "@/shared/ui/PageHeader";

export function ChannelMarkupConfigPage() {
  const markups = useQuery({ queryKey: ["pricing", "markups"], queryFn: pricingApi.listMarkups });
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Channel | null>(null);
  const [pct, setPct] = useState<number>(0);
  const [reason, setReason] = useState("");

  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();   // REASON-1
  const submit = useMutation({
    mutationFn: async () => {
      const cur = markups.data?.find((m) => m.channel === editing);
      const r = await askReason({
        scene: "price_change", title: "提交加成率变更申请", subtitle: CHANNEL_LABEL[editing!],
        changes: [{ label: "渠道 markup", before: cur ? `${cur.markup_pct}%` : "—", after: `${pct}%` }], confirmText: "确认并提交",
      });
      if (!r) throw new ReasonCancelled();
      return pricingApi.submitChange({ target_kind: "channel_markup", target_ref: editing!, proposed_pct: pct, reason: r.reason ?? reason, reason_code: r.reason_code });
    },
    onSuccess: () => {
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["pricing"] });
    },
    onError: (e) => { if (!isReasonCancelled(e)) alert((e as Error).message || "提交失败"); },
  });

  if (markups.isLoading || !markups.data) return <LoadingState />;

  return (
    <PageShell
      title="渠道 markup 配置"
      subtitle="4 渠道差异化加价率 · 修改 → 提交 D3B → 老板审批 → 生效"
    >
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">渠道</th>
              <th className="px-4 py-3 text-right">当前 markup</th>
              <th className="px-4 py-3 text-left">含税 / 含费</th>
              <th className="px-4 py-3 text-left">状态</th>
              <th className="px-4 py-3 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {markups.data.map((m) => (
              <tr key={m.channel} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-900">{m.channel_label}</td>
                <td className="px-4 py-3 text-right">
                  <span className="text-[18px] font-bold">{m.markup_pct}%</span>
                </td>
                <td className="px-4 py-3 text-[11px] text-slate-500">
                  {m.tax_included ? "✓ 含税" : "✗ 不含税"} · {m.fee_included ? "✓ 含费" : "✗ 不含费"}
                </td>
                <td className="px-4 py-3">
                  {m.pending_change_id ? (
                    <Pill tone="warning">有待审变更</Pill>
                  ) : (
                    <Pill tone="success">正常</Pill>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <Can perm="pricing.policies.update"><button
                    type="button"
                    disabled={!!m.pending_change_id}
                    onClick={() => {
                      setEditing(m.channel);
                      setPct(m.markup_pct);
                    }}
                    className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-50"
                  >
                    <Edit3 className="h-3 w-3" /> 申请变更
                  </button></Can>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
          <h3 className="mb-3 text-base font-semibold text-amber-900">
            申请变更 · {CHANNEL_LABEL[editing]}
          </h3>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium text-amber-800">新 markup (%)</span>
              <input
                type="number"
                step="0.5"
                value={pct}
                onChange={(e) => setPct(Number(e.target.value))}
                className="h-9 w-32 rounded-lg border border-amber-300 px-3 text-[13px]"
              />
            </label>
            <label className="flex-1 flex flex-col gap-1 min-w-[200px]">
              <span className="text-[11px] font-medium text-amber-800">理由 *</span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="h-9 rounded-lg border border-amber-300 px-3 text-[13px]"
                placeholder="提交给老板审批"
              />
            </label>
            <button
              type="button"
              disabled={!reason || submit.isPending}
              onClick={() => submit.mutate()}
              className="h-9 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white disabled:opacity-50"
            >
              提交 D3B 审批
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-[13px]"
            >
              取消
            </button>
          </div>
        </div>
      )}
    {reasonDialog}
    </PageShell>
  );
}
